/**
 * Treatment plan rules engine: charted dental findings in, suggested plan out.
 *
 * Pure and synchronous, with no network call, so it works offline on the
 * Diagnosis Pad. The dentist always has the last word:
 *
 *   - A favourite for a condition replaces the default rule for it.
 *   - Items the dentist added or edited ("manual"), and items already acted on
 *     (any status but "proposed"), are kept as they are and never duplicated.
 *   - Removing a suggestion marks it "cancelled" rather than deleting it, so a
 *     re-run does not bring it back.
 *   - A suggestion that survives a re-run keeps its id, so offline saves and
 *     their retries refer to the same item.
 */
import {
  PROCEDURE_BY_CODE,
  PROCEDURES,
  type ConditionKey,
  type ProcedureCode,
  type ProcedureScope,
} from "../constants/dental.constants";
import type { DentalFinding, PlanItem } from "../schemas/dental.schema";

// ─── FDI helpers ──────────────────────────────────────────────────────────────

export const isPrimaryTooth = (fdi: number): boolean => Math.floor(fdi / 10) >= 5;

/** Quadrant 1-4; primary quadrants 5-8 map onto the same four. */
export const quadrantOf = (fdi: number): 1 | 2 | 3 | 4 =>
  (((Math.floor(fdi / 10) - 1) % 4) + 1) as 1 | 2 | 3 | 4;

/** Position 1-3 is anterior, 4-5 premolar, 6-8 molar (permanent teeth). */
export function rctFor(fdi: number): ProcedureCode {
  const position = fdi % 10;
  if (position <= 3) return "rct_anterior";
  if (position <= 5) return "rct_premolar";
  return "rct_molar";
}

// ─── Rules ────────────────────────────────────────────────────────────────────

/** Default crown when a tooth needs one; a favourite can choose another. */
const DEFAULT_CROWN: ProcedureCode = "crown_pfm";

/**
 * Default procedures for one condition on one tooth. Scope comes from the
 * catalogue: a quadrant procedure lands on the tooth's quadrant, a mouth
 * procedure once for the whole mouth.
 */
function defaultRule(condition: ConditionKey, fdi: number, hasCrown: boolean): ProcedureCode[] {
  const primary = isPrimaryTooth(fdi);
  const crown = hasCrown ? [] : [DEFAULT_CROWN];
  switch (condition) {
    case "caries":                return primary ? ["gic_restoration"] : ["composite_restoration"];
    case "deep_caries":           return primary
      ? ["pulpotomy", "stainless_steel_crown"]
      : ["iopa", "indirect_pulp_capping", "composite_restoration"];
    case "reversible_pulpitis":   return primary ? ["pulpotomy"] : ["indirect_pulp_capping", "composite_restoration"];
    case "irreversible_pulpitis": return primary
      ? ["pulpectomy", "stainless_steel_crown"]
      : ["iopa", rctFor(fdi), ...crown];
    case "periapical_abscess":    return primary
      ? ["extraction", "space_maintainer"]
      : ["iopa", rctFor(fdi), ...crown];
    case "fracture":              return primary ? ["gic_restoration"] : ["iopa", "composite_restoration"];
    case "impacted":              return ["opg", "impaction_removal"];
    case "root_stump":            return primary ? ["extraction"] : ["iopa", "extraction"];
    case "attrition":             return primary ? [] : ["desensitising"];
    case "stain":                 return ["oral_prophylaxis"];
    case "pyorrhea":              return primary ? ["oral_prophylaxis"] : ["srp"];
    case "recession":             return primary ? [] : ["desensitising"];
    case "root_canal_treated":    return primary ? [] : crown;
    case "missing":               return primary ? ["space_maintainer"] : ["implant"];
    case "crown":
    case "implant":               return [];
  }
}

function mobilityRule(grade: 1 | 2 | 3, fdi: number): ProcedureCode[] {
  // A mobile milk tooth is usually exfoliating: only grade III needs anything.
  if (isPrimaryTooth(fdi)) return grade === 3 ? ["extraction"] : [];
  if (grade === 3) return ["extraction"];
  return grade === 2 ? ["srp", "splinting"] : ["srp"];
}

// Per-tooth precedence. When a tooth has suggestions from several tracks only
// the highest is kept, so a tooth is never planned for both RCT and extraction,
// or for a filling under an RCT.
const TRACK: Partial<Record<ProcedureCode, number>> = {
  // Pulp capping is placed under the filling in the same sitting: same track.
  composite_restoration: 1, gic_restoration: 1, amalgam_restoration: 1,
  indirect_pulp_capping: 1, direct_pulp_capping: 1,
  pulpotomy: 2,
  rct_anterior: 3, rct_premolar: 3, rct_molar: 3, re_rct: 3, pulpectomy: 3,
  extraction: 4, surgical_extraction: 4, impaction_removal: 4,
};
const EXTRACTION_TRACK = 4;
/** All that survives on a tooth planned for extraction. */
const KEPT_WITH_EXTRACTION = new Set<string>([
  // Typed through the array so a misspelt code fails to compile.
  ...(["iopa", "opg", "space_maintainer"] satisfies ProcedureCode[]),
]);

// ─── Engine ───────────────────────────────────────────────────────────────────

/** One procedure of a saved template, as returned by /doctor-templates. */
export interface TemplateItem {
  procedure_code: string;
  label: string;
  sittings_planned: number;
  scope: ProcedureScope;
}

/** The doctor's favourite plan per condition. */
export type Favourites = Partial<Record<ConditionKey, TemplateItem[]>>;

export interface SuggestPlanInput {
  findings: DentalFinding[];
  /** The plan as it stands, so the dentist's own work is kept. */
  current?: PlanItem[];
  favourites?: Favourites;
  /** Injectable for tests; defaults to crypto.randomUUID. */
  newId?: () => string;
}

interface Candidate {
  code: string;
  label: string;
  sittings: number;
  scope: ProcedureScope;
}

const fromCatalogue = (code: ProcedureCode): Candidate => {
  const p = PROCEDURE_BY_CODE[code];
  return { code, label: p.label, sittings: p.sittings, scope: p.scope };
};

const fromTemplate = (t: TemplateItem): Candidate => ({
  code: t.procedure_code, label: t.label, sittings: t.sittings_planned, scope: t.scope,
});

function candidatesForTooth(finding: DentalFinding, favourites: Favourites): Candidate[] {
  const { tooth, conditions } = finding;
  // Nothing else on a missing tooth can be treated.
  const active: ConditionKey[] = conditions.includes("missing") ? ["missing"] : conditions;
  const hasCrown = active.includes("crown") || active.includes("implant");

  const out: Candidate[] = [];
  for (const condition of active) {
    const favourite = favourites[condition];
    if (favourite && favourite.length > 0) out.push(...favourite.map(fromTemplate));
    else out.push(...defaultRule(condition, tooth, hasCrown).map(fromCatalogue));
  }
  if (finding.mobility_grade && !active.includes("missing")) {
    out.push(...mobilityRule(finding.mobility_grade, tooth).map(fromCatalogue));
  }
  return applyPrecedence(out);
}

function applyPrecedence(candidates: Candidate[]): Candidate[] {
  const toothLevel = candidates.filter((c) => c.scope === "tooth");
  const trackOf = (code: string) => TRACK[code as ProcedureCode] ?? 0;
  const top = Math.max(0, ...toothLevel.map((c) => trackOf(c.code)));
  return candidates.filter((c) => {
    if (c.scope !== "tooth") return true;
    if (top === EXTRACTION_TRACK) return trackOf(c.code) === EXTRACTION_TRACK || KEPT_WITH_EXTRACTION.has(c.code);
    const track = trackOf(c.code);
    return track === 0 || track === top;
  });
}

const keyOf = (code: string, tooth: number | null, quadrant: number | null) =>
  `${code}|${tooth ?? ""}|${quadrant ?? ""}`;

const itemKey = (i: PlanItem) => keyOf(i.procedure_code, i.tooth, i.quadrant);

/** The dentist's own work: never replaced, and never duplicated by a suggestion. */
const isLocked = (i: PlanItem) => i.source === "manual" || i.status !== "proposed";

export function suggestPlan({
  findings,
  current = [],
  favourites = {},
  newId = () => crypto.randomUUID(),
}: SuggestPlanInput): PlanItem[] {
  const locked = current.filter(isLocked);
  const lockedKeys = new Set(locked.map(itemKey));
  const reusable = new Map(current.filter((i) => !isLocked(i)).map((i) => [itemKey(i), i]));

  const suggested = new Map<string, PlanItem>();
  for (const finding of findings) {
    for (const c of candidatesForTooth(finding, favourites)) {
      const tooth = c.scope === "tooth" ? finding.tooth : null;
      const quadrant = c.scope === "quadrant" ? quadrantOf(finding.tooth) : null;
      const key = keyOf(c.code, tooth, quadrant);
      if (lockedKeys.has(key) || suggested.has(key)) continue;
      suggested.set(key, reusable.get(key) ?? {
        id: newId(),
        tooth,
        quadrant,
        procedure_code: c.code,
        label: c.label,
        sittings_planned: c.sittings,
        status: "proposed",
        source: "auto",
      });
    }
  }
  return sortPlan([...locked, ...suggested.values()]);
}

// ─── Ordering ─────────────────────────────────────────────────────────────────

const CATALOGUE_ORDER = new Map<string, number>(PROCEDURES.map((p, i) => [p.code, i]));

const scopeRank = (i: PlanItem) => (i.tooth !== null ? 2 : i.quadrant !== null ? 1 : 0);

/** Mouth-level first, then by quadrant, then by tooth; catalogue order within a tooth. */
export function sortPlan(items: PlanItem[]): PlanItem[] {
  return [...items].sort((a, b) =>
    scopeRank(a) - scopeRank(b)
    || (a.quadrant ?? 0) - (b.quadrant ?? 0)
    || (a.tooth ?? 0) - (b.tooth ?? 0)
    || (CATALOGUE_ORDER.get(a.procedure_code) ?? Infinity) - (CATALOGUE_ORDER.get(b.procedure_code) ?? Infinity)
    || a.label.localeCompare(b.label),
  );
}
